/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
	Avatar,
  List,
  Tag
} from "antd";
import {
  UserOutlined,
  UsergroupAddOutlined,
  BorderOuterOutlined
} from '@ant-design/icons';
import { getTextColorBasedOnBg } from '../../../Utils/color_utils'

const { Group } = Avatar;

export const PlayerInfoCard = ({ selectionList, sideId })  => {
  const [metrics, setMetrics] = useState({});
  const [players, setPlayers] = useState([]);
  const [cardContent, setCardContent] = useState();

  const {
    positions,
    playerGroups: groups,
    allPlayers,
    info: { team_colors: teamColors },
	} = useSelector(({ team }) => team);

  const colorList = teamColors.all;
  const cardBgColor = sideId == "left" ? teamColors?.primary : teamColors.secondary;
  const color = getTextColorBasedOnBg(cardBgColor);

  const getMetricIdListByType = (selectionList) => {
    let metrics = {};

    for(let i = 0; i < selectionList.length; i++)
    {
      let metric_split = selectionList[i].split("-");
      let metric_type = metric_split[0], metric_id = metric_split[1];
      if (metric_type in metrics) metrics[metric_type].push(metric_id);
      else metrics[metric_type] = [metric_id];
    }

    return metrics;
  }

  useEffect(() => {
    if (selectionList.length > 0 && !(selectionList.length === 1 && selectionList[0] === 'full-team')) {
      
      const metrics = getMetricIdListByType(selectionList);
      let playerObjs, groupObjs;
      const playerIdList = metrics?.player;
      const groupIdList = metrics?.group;
      const positionList = metrics?.pos;

      if (playerIdList && playerIdList.length > 0) {
        playerObjs = allPlayers.filter(player => playerIdList.includes(player.player_id.toString()));
        if (playerObjs.length > 1) {
          playerObjs = playerObjs.sort((a, b) => playerIdList.indexOf(a.player_id.toString()) - playerIdList.indexOf(b.player_id.toString()));
        }
      }

      if (groupIdList && groupIdList.length > 0) {
        groupObjs = groups.filter(group => groupIdList.includes(group.player_group_id.toString()));
        if (groupObjs.length > 1) {
          groupObjs = groupObjs.sort((a, b) => groupIdList.indexOf(a.player_group_id.toString()) - groupIdList.indexOf(b.player_group_id.toString()));
        }
      }

      if (playerObjs && playerObjs.length > 0) {
        if (playerObjs.length === 1 && !groupIdList && !positionList) {
          const player = playerObjs[0];
          //Only one player is selected
          let textColor = getTextColorBasedOnBg(colorList[0]);
          setCardContent(
            <div 
              className="player-info-card" 
              style={{backgroundColor: cardBgColor, color: color}}
            >
              <div className="avatar-container">
                <Avatar 
                  src={player["headshot"] ? player["headshot"] : undefined}
                  style={{ color: textColor, backgroundColor: colorList[0], border: `1px solid ${color}`}}
                  size={60}
                  gap={4}
                  shape="circle"
                >
                  {player["first_name"][0]}
                </Avatar>
              </div>
              <div className="info-container">
                <div className="player-info-card-position">{`${player["position"]}`}</div>
                <div>
                  <span className="player-info-card-jersey-num">{`${player["number"]}`}</span>
                  <span className="player-info-card-player-name">{`${player["first_name"]} ${player["last_name"]}`}</span>
                </div>
              </div>  
            </div>
          );
        }
        else {
          //Multiple players are selected
          getCardContent(metrics, playerObjs, groupObjs);
        }
      }
      else {
        //No players are selected
        getCardContent(metrics, playerObjs, groupObjs);
      }
    }
    else {
      setCardContent();
    }

  }, [selectionList])

  const getCardContent = (metrics, playerObjs, groupObjs) => {
    let colorIdx = 0, elementIdx = 0 , textColor, avatars = [], tags = [];

    Object.entries(metrics).forEach(([metric_type, metric_ids]) => {
      if (metric_type === "player") {
        playerObjs.map((player) => {
          if (colorIdx >= colorList.length) colorIdx = (colorIdx - colorList.length);
          textColor = getTextColorBasedOnBg(colorList[colorIdx]);
          
          avatars.push(
            <Avatar 
              key={elementIdx}
              style={{ color: textColor, backgroundColor: colorList[colorIdx], border: `1px solid ${color}`}}
              size={50}
              gap={4}
              shape="circle"
              src={player["headshot"] ? player["headshot"] : undefined}
            >
              {player["first_name"][0]}
            </Avatar>
          )

          textColor = getTextColorBasedOnBg(colorList[0]);
          tags.push(
            <Tag 
              key={elementIdx}
              color={colorList[0]}
              style={{ border: `1px solid ${color}`, borderRadius: '5px'}}
              icon={<UserOutlined style={{ color: color }}/>}
              className="player-info-card-tag"
            >
              <span
                style={{ color: textColor }}
              >
                {`${player["first_name"]} ${player["last_name"]}`}
              </span>
            </Tag>
          )

          colorIdx += 1;
          elementIdx += 1;
        })
      }
      else if (metric_type === "pos") {
        metric_ids.map((pos) => {
          if (colorIdx >= colorList.length) colorIdx = (colorIdx - colorList.length);
          textColor = getTextColorBasedOnBg(colorList[colorIdx]);
          
          avatars.push(
            <Avatar 
              key={elementIdx}
              style={{ color: textColor, backgroundColor: colorList[colorIdx], border: `1px solid ${color}`}}
              size={50}
              gap={4}
              shape="circle"
            >
              {pos}
            </Avatar>
          )

          textColor = getTextColorBasedOnBg(colorList[1]);
          tags.push(
            <Tag 
              key={elementIdx}
              color={colorList[1]}
              style={{ border: `1px solid ${color}`, borderRadius: '5px'}}
              icon={<BorderOuterOutlined style={{ color: color }}/>}
              className="player-info-card-tag"
            >
              <span
                style={{ color: textColor }}
              >
                {`Pos: ${pos}`}
              </span>
            </Tag>
          )

          colorIdx += 1;
          elementIdx += 1;
        })
      }
      else if (metric_type === "group") {
        groupObjs.map((group) => {
          if (colorIdx >= colorList.length) colorIdx = (colorIdx - colorList.length);
          textColor = getTextColorBasedOnBg(colorList[colorIdx]);
          
          avatars.push(
            <Avatar 
              key={elementIdx}
              style={{ color: textColor, backgroundColor: colorList[colorIdx], border: `1px solid ${color}`}}
              size={50}
              gap={4}
              shape="circle"
            >
              {group["name"]}
            </Avatar>
          )

          textColor = getTextColorBasedOnBg(colorList[2]);
          tags.push(
            <Tag 
              key={elementIdx}
              color={colorList[2]}
              style={{ border: `1px solid ${color}`, borderRadius: '5px'}}
              icon={<UsergroupAddOutlined style={{ color: color }}/>}
              className="player-info-card-tag"
            >
              <span
                style={{ color: textColor }}
              >
                {`Group: ${group["name"]}`}
              </span>
            </Tag>
          )

          colorIdx += 1;
          elementIdx += 1;
        })
      }
    })

    if (avatars.length > 0 && tags.length > 0) {
      setCardContent(
        <div 
          className="player-info-card" 
          style={{backgroundColor: cardBgColor, color: color}}
        >
          <Avatar.Group
            maxCount={3}
            size={50}
            className="multiple-avatar-container"
          >
            {
              avatars.map((avatar) => avatar)
            }
          </Avatar.Group>
          <div className="tag-container">
            {
              tags.map((tag) => tag)
            }
          </div>
        </div>
      );
    }
    else {
      setCardContent();
    }
  }

  return (
    <>
      {cardContent}
    </>
  );
}
